import React from 'react';
import PropTypes from 'prop-types';
import { serialize } from 'core/utils';

/**
 * Is this asset URL from contentful?
 *
 * @param {String} imageUrl
 * @return {bool} True if from `ctfassets.net`
 */
export const isContentful = (imageUrl) => imageUrl && imageUrl.includes("ctfassets.net");

/**
 * Use Contentful Image API to get performant asset url
 * https://www.contentful.com/developers/docs/references/images-api/
 *
 * @param {String} imageUrl
 * @param {Object} query: {key: value} Image API query parameters
 * @return {String} webpUrl
 */
const webpUrl = (imageUrl, query) => {
	// If changing, see also: `webpify` jinja filter
	query = query || {};
	query["fm"] = "webp";
	query["q"] = "85";
	return `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}${serialize(query)}`;
};

/**
 * Lazy-capable, srcset aware `img` element
 * will auto-determine next-gen contentful asset url
 * to style, if you put this in `StyledWrapper` do `StyledWrapper img { ... }`
 */
const ContentfulImage = ({ src, className, lazy, query, ...props }) => {
	const imgProps = !lazy ? { src, className } : {
		'data-src': src,
		className: `${className} lazyload`,
	};

	if (!isContentful(src)) {
		return (
			<img {...imgProps} {...props} />
		);
	}

	const webpSrc = webpUrl(src, query);
	const sourceProps = !lazy ? { srcSet: webpSrc } : { 'data-srcset': webpSrc };
	return (
		<picture>
			<source {...sourceProps} type="image/webp" />
			<img {...imgProps} {...props} />
		</picture>
	);
};


ContentfulImage.propTypes = {
	src: PropTypes.string.isRequired,
	className: PropTypes.string,
	query: PropTypes.object,
	lazy: PropTypes.bool,
};

ContentfulImage.defaultProps = {
	className: "",
	query: null,
	lazy: false,
};

export default ContentfulImage;
